import { useState, useEffect } from "react";
import ObjectConfigApi from "../../../api/ObjectConfigApi";
import DataApi from "../../../api/DataApi";
import PubSub from "pubsub-js";

/**
 * Custom hook for managing list view filters
 */
export const useListViewFilters = (objectName, listViews, listViewName) => {
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [externalFilters, setExternalFilters] = useState([]);
  const [externalFilterLogic, setExternalFilterLogic] = useState("AND");
  const [availableFields, setAvailableFields] = useState([]);

  // Sync filters when list view changes
  useEffect(() => {
    const currentListView = listViews.find(view => view.name === listViewName);
    if (currentListView?.config) {
      if (currentListView.config.filters && currentListView.config.filters.length > 0) {
        setExternalFilters(currentListView.config.filters);
      } else {
        setExternalFilters([]);
      }

      if (currentListView.config.logic) {
        setExternalFilterLogic(currentListView.config.logic);
      } else {
        setExternalFilterLogic("AND");
      }
    } else {
      setExternalFilters([]);
      setExternalFilterLogic("AND");
    }
  }, [listViews, listViewName]);

  const toggleFilterPanel = async () => {
    if (!filterPanelOpen) {
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        const result = response.data;
        if (result?.fields?.length > 0) {
          const allFields = result.fields.map((f) => ({
            id: f.id,
            label: f.field_label,
            name: f.field_name,
            dataType: f.data_type || 'text',
          }));
          setAvailableFields(allFields);
        }

        const currentListView = listViews.find(view => view.name === listViewName);
        if (currentListView && currentListView.config) {
          if (currentListView.config.filters && currentListView.config.filters.length > 0) {
            setExternalFilters(currentListView.config.filters);
          } else {
            setExternalFilters([]);
          }

          if (currentListView.config.logic) {
            setExternalFilterLogic(currentListView.config.logic);
          } else {
            setExternalFilterLogic("AND");
          }
        } else {
          setExternalFilters([]);
          setExternalFilterLogic("AND");
        }
      } catch (error) {
        console.error("Error fetching fields for filter:", error);
      }
    }
    setFilterPanelOpen(!filterPanelOpen);
  };

  const addExternalFilter = () => {
    const newFilter = {
      id: Date.now(),
      field: "",
      operator: "equals",
      value: "",
    };
    setExternalFilters([...externalFilters, newFilter]);
  };

  const removeExternalFilter = (filterId) => {
    setExternalFilters(externalFilters.filter(f => f.id !== filterId));
  };

  const updateExternalFilter = (filterId, field, value) => {
    setExternalFilters(externalFilters.map(f =>
      f.id === filterId ? { ...f, [field]: value } : f
    ));
  };

  const getOperatorsForField = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    if (!field) return [];

    const dataType = field.dataType?.toLowerCase();

    switch (dataType) {
      case 'date':
      case 'datetime':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'between', label: 'Between' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
      case 'number':
      case 'integer':
      case 'decimal':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'between', label: 'Between' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
      case 'boolean':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
      default:
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Not Contains' },
          { value: 'starts_with', label: 'Starts With' },
          { value: 'ends_with', label: 'Ends With' },
          { value: 'is_null', label: 'Is Null' },
          { value: 'is_not_null', label: 'Is Not Null' }
        ];
    }
  };

  const isOperatorValueRequired = (operator) => {
    return !['is_null', 'is_not_null'].includes(operator);
  };

  const getFieldDataType = (fieldName) => {
    const field = availableFields.find(f => f.name === fieldName);
    return field?.dataType?.toLowerCase() || 'text';
  };

  const applyExternalFilters = async (setRefreshTrigger) => {
    try {
      const currentListView = listViews.find(view => view.name === listViewName);
      if (!currentListView) {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "No List View",
          message: "No list view selected. Please select a list view first."
        });
        return;
      }

      const validFilters = externalFilters.filter(f => f.field && f.operator);
      const updatedConfig = {
        ...currentListView.config,
        filters: validFilters,
        logic: externalFilterLogic
      };

      const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const updateResponse = await configApi.update({
        config: updatedConfig
      }, currentListView.id);

      if (updateResponse?.data?.success) {
        setRefreshTrigger(prev => prev + 1);
        setFilterPanelOpen(false);
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Filters Applied",
          message: `Filters applied successfully to "${currentListView.label || currentListView.name}"`
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Filter Error",
          message: "Failed to save filters. Please try again."
        });
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Filter Error",
        message: "Error applying filters: " + (error.response?.data?.error || error.message)
      });
    }
  };

  const clearExternalFilters = async (setRefreshTrigger) => {
    try {
      const currentListView = listViews.find(view => view.name === listViewName);
      if (!currentListView) {
        setExternalFilters([]);
        setExternalFilterLogic("AND");
        return;
      }

      const updatedConfig = {
        ...currentListView.config,
        filters: [],
        logic: "AND"
      };

      const configApi = new DataApi(`objectconfig/listviews/${objectName}`);
      const updateResponse = await configApi.update({
        config: updatedConfig
      }, currentListView.id);

      if (updateResponse?.data?.success) {
        setExternalFilters([]);
        setExternalFilterLogic("AND");
        setRefreshTrigger(prev => prev + 1);
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Filters Cleared",
          message: `Filters cleared from "${currentListView.label || currentListView.name}"`
        });
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Filter Error",
          message: "Failed to clear filters. Please try again."
        });
      }
    } catch (error) {
      console.error("Error clearing filters:", error);
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Filter Error",
        message: "Error clearing filters: " + (error.response?.data?.error || error.message)
      });
    }
  };

  const activeFiltersCount = externalFilters.filter(f => f.field && f.operator).length;
  const hasActiveFilters = activeFiltersCount > 0;

  return {
    filterPanelOpen,
    externalFilters,
    externalFilterLogic,
    availableFields,
    activeFiltersCount,
    hasActiveFilters,
    toggleFilterPanel,
    addExternalFilter,
    removeExternalFilter,
    updateExternalFilter,
    getOperatorsForField,
    isOperatorValueRequired,
    getFieldDataType,
    applyExternalFilters,
    clearExternalFilters,
    setExternalFilters,
    setExternalFilterLogic,
    setFilterPanelOpen
  };
};
